import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import { subscribeToPush, sendSubscriptionToServer } from "@/lib/pwa";
import { updateManifestStartUrl } from "@/lib/manifest";

interface AuthState {
  role: "admin" | "client" | null;
  jwt: string | null;
  passcode: string | null;
  user: {
    _key: string;
    name: string;
    icon: string;
    description: string;
  } | null;
}

interface AuthContextType extends AuthState {
  setAuth: (state: Partial<AuthState>) => void;
  logout: () => void;
  getHeaders: () => Record<string, string>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

const STORAGE_KEY = "choreapp_passcode";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        // ignore
      }
    }
    return { role: null, jwt: null, passcode: null, user: null };
  });

  const setAuth = useCallback((partial: Partial<AuthState>) => {
    setState((prev) => {
      const next = { ...prev, ...partial };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
    // Keep the PWA manifest link in sync so installation always
    // opens this user's passcode/login page.
    if (partial.role && partial.jwt) {
      const manifestHref = `/manifest/${partial.role}/${partial.jwt}`;
      updateManifestStartUrl(manifestHref);
    }
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    setState({ role: null, jwt: null, passcode: null, user: null });
  }, []);

  const getHeaders = useCallback(() => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (state.passcode) {
      headers["X-Passcode"] = state.passcode;
    }
    return headers;
  }, [state.passcode]);

  // Subscribe to push notifications after login
  useEffect(() => {
    if (!state.role || !state.jwt || !state.passcode) return;

    // Fire-and-forget push subscription setup
    setTimeout(async () => {
      try {
        // Fetch VAPID public key
        const vapidResp = await fetch("/api/push/vapid-public-key");
        if (!vapidResp.ok) return;
        const { publicKey } = await vapidResp.json();

        // Subscribe to push notifications
        const subscription = await subscribeToPush(publicKey);
        if (!subscription) return;

        // Send subscription to server
        if (state.role && state.jwt) {
          await sendSubscriptionToServer(
            subscription,
            state.role,
            state.jwt,
            getHeaders(),
          );
        }
      } catch (err) {
        // Silent fail - push is optional
        console.debug("Push subscription setup skipped:", err);
      }
    }, 100);
  }, [state.role, state.jwt, state.passcode, getHeaders]);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        setAuth,
        logout,
        getHeaders,
        isAuthenticated: !!state.jwt && !!state.passcode,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
